/**
 * Single source of truth for a connectable source provider's identity in the
 * SPA: its connection-panel copy, its sidebar/nav placement, its feed
 * filters, its attention-count predicate, and its Settings card. Every other
 * module that used to hand-roll one of these per provider (the connection
 * registry, the sidebar model, the layout titles, the Settings source cards)
 * now derives from `SOURCE_PROVIDERS` — adding a provider means adding one
 * entry here. See `docs/ADDING_A_PROVIDER.md`.
 *
 * `sourceErrors.ts`'s per-provider error-message quirks (Jira's stale-API
 * hint, GitHub's HYDRATE_EMPTY / 403 copy, Slack's code-suffixed messages)
 * are NOT derived here — they stay as small, provider-specific functions in
 * that file. Unifying them into data would obscure more than it would save;
 * a new provider adds one function + one dispatch line there instead.
 */

import type { ConnectableSource } from './providerIds';
import { CONNECTABLE_SOURCES } from './providerIds';
import type { AttentionItem, ViewMode } from './types';
import type { CapabilityId } from './capabilityNavigation';
import type { BrandType } from '../ui/common/BrandIcon';
import { GITHUB_AXES, GMAIL_AXES, JIRA_AXES, SLACK_AXES } from './filtering';
import type { FilterAxis } from './multiAxisFiltering';

export interface SourceProviderDefinition {
  id: ConnectableSource;
  // Connection surface (SourceConnectionPanel / SourceGate copy)
  label: string;
  noun: string;
  description: string;
  emptyMessage: string;
  filterEmptyMessage: string;
  skeletonHeight: number;
  // Navigation (sidebar, mobile nav, layout titles, capability routing)
  view: ViewMode;
  capability: CapabilityId;
  navLabel: string;
  viewSubtitle: string;
  shortcut: string;
  brand: BrandType;
  // Feed
  axes: FilterAxis<AttentionItem>[];
  /** Predicate deciding whether an item of this source counts toward the sidebar badge. */
  countsAsAttention: (item: AttentionItem) => boolean;
  // Settings
  settingsCard: { label: string; description: string };
}

export const SOURCE_PROVIDERS: Record<ConnectableSource, SourceProviderDefinition> = {
  jira: {
    id: 'jira',
    label: 'Jira',
    noun: 'tus tickets',
    description: 'Actividad reciente, bloqueos y revisiones de Jira en un solo lugar.',
    emptyMessage: 'No hay actividad reciente de Jira.',
    filterEmptyMessage: 'No hay tickets de Jira para el filtro seleccionado.',
    skeletonHeight: 160,
    view: 'jira',
    capability: 'tasks',
    navLabel: 'Tareas',
    viewSubtitle: 'Actividad reciente sin filtros · Jira',
    shortcut: '2',
    brand: 'jira',
    axes: JIRA_AXES,
    countsAsAttention: () => true,
    settingsCard: { label: 'Jira', description: 'Sitio, acceso y configuración de trabajo' },
  },
  slack: {
    id: 'slack',
    label: 'Slack',
    noun: 'tus hilos',
    description: 'Mensajes directos, menciones e hilos recientes relacionados contigo.',
    emptyMessage: 'No hay actividad reciente de Slack.',
    filterEmptyMessage: 'No hay hilos de Slack que coincidan con el filtro.',
    skeletonHeight: 150,
    view: 'slack',
    capability: 'messages',
    navLabel: 'Mensajes',
    viewSubtitle: 'Actividad reciente sin filtros · Slack',
    shortcut: '3',
    brand: 'slack',
    axes: SLACK_AXES,
    // The Slack badge counts only actionable threads (ballInCourt: you); a
    // thread the user is merely waiting on must not inflate the count.
    countsAsAttention: (item) => item.st === 'you',
    settingsCard: { label: 'Slack', description: 'Hilos, respuestas y preferencias de voz' },
  },
  github: {
    id: 'github',
    label: 'GitHub',
    noun: 'tus pull requests',
    description: 'Pull requests, revisiones solicitadas y estado de CI en un solo lugar.',
    emptyMessage: 'No hay actividad reciente de pull requests.',
    filterEmptyMessage: 'No hay Pull Requests para el filtro seleccionado.',
    skeletonHeight: 170,
    view: 'github',
    capability: 'code',
    navLabel: 'Código',
    viewSubtitle: 'Actividad reciente sin filtros · GitHub',
    shortcut: '4',
    brand: 'github',
    axes: GITHUB_AXES,
    countsAsAttention: () => true,
    settingsCard: { label: 'GitHub', description: 'Repositorios, revisiones y permisos' },
  },
  gmail: {
    id: 'gmail',
    label: 'Gmail',
    noun: 'tu correo',
    description: 'Todos los hilos recientes de tu bandeja de entrada.',
    emptyMessage: 'No hay correo reciente en tu bandeja de entrada.',
    filterEmptyMessage: 'No hay correos de Gmail para el filtro seleccionado.',
    skeletonHeight: 140,
    view: 'gmail',
    capability: 'mail',
    navLabel: 'Correo',
    viewSubtitle: 'Bandeja reciente sin filtros · Gmail',
    shortcut: '7',
    brand: 'gmail',
    axes: GMAIL_AXES,
    countsAsAttention: (item) => item.unread === true,
    settingsCard: { label: 'Gmail', description: 'Cuenta de Google y reglas de atención' },
  },
};

/** Sidebar / mobile-nav display order. */
export const SOURCE_PROVIDER_ORDER: readonly ConnectableSource[] = [
  'jira',
  'slack',
  'github',
  'gmail',
];

/** Settings "Proveedores" accordion order (kept distinct from nav order deliberately). */
export const SETTINGS_PROVIDER_ORDER: readonly ConnectableSource[] = [
  'slack',
  'jira',
  'github',
  'gmail',
];

export function sourceProviderDefinition(source: ConnectableSource): SourceProviderDefinition {
  return SOURCE_PROVIDERS[source];
}

// Compile-time guard: CONNECTABLE_SOURCES and SOURCE_PROVIDERS must stay in
// lockstep, or adding an id to one without the other would silently produce
// `undefined` lookups at runtime instead of a typecheck failure.
CONNECTABLE_SOURCES.forEach((id) => SOURCE_PROVIDERS[id]);
